import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { exportFreelancerData } from "@/hooks/use-freelancers";
import { useEraseFreelancer, useSetFreelancerArchived } from "@/hooks/use-freelancer-mutations";
import type { FreelancerListRow } from "@/lib/freelancers";

/** ⋯-menyen på profilen: arkiver for alle, innsyn og permanent sletting for admin. */
export function FreelancerActionsMenu({ freelancer }: { freelancer: FreelancerListRow }) {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const archive = useSetFreelancerArchived();
  const erase = useEraseFreelancer();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typedName, setTypedName] = useState("");

  const downloadExport = async () => {
    try {
      const data = await exportFreelancerData(freelancer.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `innsyn-${freelancer.name.replace(/[^\w-]+/g, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`Kunne ikke eksportere: ${(err as Error).message}`);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Flere valg">
            <SrIcon name="more" className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => archive.mutate({ id: freelancer.id, archived: !freelancer.archived_at })}>
            {freelancer.archived_at ? "Gjenopprett" : "Arkiver"}
          </DropdownMenuItem>
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={downloadExport}>Eksporter (innsyn)</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onSelect={() => { setTypedName(""); setConfirmOpen(true); }}>
                Slett permanent
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slett {freelancer.name} permanent?</AlertDialogTitle>
            <AlertDialogDescription>
              Profilen, vurderingene, kommentarene og filene slettes og kan ikke hentes tilbake. Bruk dette når personen ber om
              sletting. Ellers holder det å arkivere. Skriv navnet for å bekrefte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} aria-label="Skriv navnet for å bekrefte" />
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={typedName.trim() !== freelancer.name || erase.isPending}
              onClick={() => erase.mutate(freelancer.id, { onSuccess: () => navigate("/frilansere") })}
            >
              Slett permanent
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
